// Copyright (c) 2017 Comsearch, Inc., All rights reserved

import React from 'react'
import { array, bool, func, object, oneOfType, string } from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { error } from 'react-notification-system-redux'
import { debounce } from 'lodash'
import ReactAutoSuggest from 'react-autosuggest'
import { UNCAUGHT_ERROR } from 'errors'
import { createRegExpWithWildcards } from 'helpers/regex'
import ErrorSimple from './ErrorSimple'

// NOTE: the prop 'optionKey' in this component is not required.
// This was solely done to allow for suggestions based on arrays of just plain strings.
// For an example of this component without an optionKey,
// See SearchAndSelect > Sets > AntennaDb > components > BasicSearch
// and in API: /antenna-specs/model-numbers

// TAKE HEED:
// The above scenario is the ONLY circumstance where an optionKey is not necessary.
// Take the Site autosuggest for example. Site Name is not unique.
// So two sites could have different data but the same name.
// if optionKey={'siteId'} wasn't used on that intance of AutoSuggest,
// the FIRST match of the selected site name would be returned,
// and no regard would be given to which entity was actually selected.

const initialState = {
  fetchDebounce: null,
  fetching: false,
  storedValue: '',
  suggestions: [],
}

// like your traditional sorter but allows
// to either sort over an array of strings, comparing a to b
// or an array of objects & which prop to sort over: a[k] to b[k]
const sorterBy = (arr, k) => (
  arr.sort((a, b) => {
    const a1 = `${k ? a[k] : a}`.toLowerCase()
    const b1 = `${k ? b[k] : b}`.toLowerCase()

    return (
      (a1 > b1 && 1) ||
      (a1 < b1 && -1) ||
      0
    )
  })
)

class AutoSuggest extends React.Component {
  static propTypes = {
    alwaysRenderSuggestions: bool,
    autoFocus: bool,
    disabled: bool,
    error: func.isRequired,
    fetchSuggestions: func,
    handleSelection: func.isRequired,
    id: string.isRequired,
    input: object.isRequired,
    label: string,
    meta: object.isRequired,
    optionKey: string,
    placeholder: string,
    renderSuggestionsContainer: func,
    sortKey: string,
    staticSuggestions: oneOfType([
      array,
      object,
    ]),
    SuggestionComponent: func,
  }

  state = initialState

  componentWillMount () {
    // if suggestions were passed down, set them to the state
    // so that they automatically render on input focus
    const { optionKey, staticSuggestions } = this.props
    if (staticSuggestions) {
      // to support both native and immutablejs datasets.
      const suggestions = (
        staticSuggestions.size
          ? staticSuggestions.toJS()
          : staticSuggestions
      )

      this.setState(() => ({
        suggestions: sorterBy(suggestions, optionKey),
      }))
    }
  }

  fetchData = async value => {
    const {
      error,
      fetchSuggestions,
      label,
      optionKey,
      sortKey,
    } = this.props

    const v = value.trim()

    // do not hit API if there's nothing to query
    if (!v) {
      return false
    }

    try {
      const { entities } = await fetchSuggestions(v)

      // ensures only update as long as it's the current value we're looking for.
      // This avoids race condition between parallel running searches
      // LEGO-11702
      if (value === this.state.storedValue) {
        this.setState(() => ({
          fetching: false,
          suggestions: sorterBy(entities, sortKey || optionKey),
        }))
      }
    } catch (err) {
      console.warn(err)
      error({
        autoDismiss: 3,
        message: `Error fetching ${label} suggestions: ${err.message || UNCAUGHT_ERROR}`,
      })

      this.setState(() => ({
        fetching: false,
        suggestions: [],
      }))
    }
  }

  debouncedFetch = debounce(this.fetchData, 300)

  // This method is called when a user presses arrow keys to move through the suggestions.
  // It is required by react-autosuggest but does not play nice with how we're using redux-form.
  // NOTE - if it is requested that the input reflect the value which is currently focused,
  // either by hover or arrow keys, this method is where you'll need to start but consider the below:
  // In a normal scenario it would update the input field with the value that was just focused.
  // However there are two problems with this:
  // 1. the input's redux-form onChange handler fires after this method is called but that event's value is the input's
  //    previous value and overrides the one set by getSuggestionValue.
  //    This can be resolved by managaing the input's value in this component state but it's not worth it because of #2.
  // 2. If a user were to arrow down to a suggested value then hit escape, the input's value
  //    would reflect what was last focused but this would not have fired onSuggestionSelected
  //    which, in many scenarios actually assigns valus either in other input fields: (like the site auto suggest)
  //    or assigns hidden values which are sent to the API in place of the visible value:
  //    (like radio code input field on the path details form where it displays the radio code but actually uses the radioId)
  // Conclusion: we'd basically have to bloat a custom onChange handler as well as tell all handleSelections how to unassign values
  //    OR
  // we can just return false here.
  getSuggestionValue = () => false

  // onChange, this will fire, cancelling any previous debouncedFetch that was queued
  // and creating a new one.
  // default value given to value because of redux-form bug
  onSuggestionsFetchRequested = ({ value = '' }) => {
    const { fetchSuggestions } = this.props
    // don't fetch if there's no fetch defined (static suggestion sets)
    // or if the value is the same as the previously fetched.
    if (!fetchSuggestions || value === this.state.storedValue) {
      return
    }

    // dump suggestions if input's empty.
    if (!value.length) {
      return this.resetState()
    }

    this.setState(() => ({
      fetching: true,
      storedValue: value,
    }))

    this.debouncedFetch.cancel()
    this.debouncedFetch(value)
  }

  // Render suggestions from state.
  renderSuggestion = s => {
    const {
      id,
      optionKey,
      SuggestionComponent,
    } = this.props

    if (SuggestionComponent) {
      return <SuggestionComponent {...s} />
    }

    const textNode = (
      optionKey
        ? s[optionKey]
        : s
    )

    return (
      <p
        data-key={textNode}
        id={`${id}-suggestion-${textNode}`}
        title={textNode}>
        {textNode}
      </p>
    )
  }

  // resets the component to its initial state.
  // in case staticSuggestions were passed down
  // to this component as props, ensure that
  // they persist upon resetting state.
  resetState = () => this.setState(({ suggestions }, { staticSuggestions }) => ({
    ...initialState,
    suggestions: staticSuggestions ? suggestions : [],
  }))

  // pass this.props.handleSelection the selected item
  // so that the parent can decide what to do with it.
  onSuggestionSelected = (e, { method, suggestion }) => {
    // user can select a suggestion by pressing enter.
    // prevent the event from triggering form submission
    if (method === 'enter') {
      e.preventDefault()
    }

    // take the selected suggestion and pass it to the handler
    this.props.handleSelection(suggestion)
  }

  makeFilteredSuggestions = () => {
    const {
      props: {
        input: { value },
        optionKey,
        staticSuggestions,
      },
      state: { suggestions },
    } = this

    // if there isn't a static set, the data set will
    // not need to be filtered
    if (!staticSuggestions) {
      return suggestions
    }

    // determines if there's an option key, if so it uses this to access the value to sort.
    // if it doens't have one, d will be a string.
    return suggestions.reduce((a, d) => {
      const v = (optionKey ? d[optionKey] : d).toLowerCase()
      const r = createRegExpWithWildcards(value.trim())
      if (v.match(r)) {
        a.push(d)
      }
      return a
    }, [])
  }

  handleBlur = ({ target: { value } }) => {
    const {
      props: { input },
      resetState,
    } = this
    input.onBlur(value.trim())
    resetState()
  }

  // Autosuggest will pass through all these props to the input element.
  // this has to be performed in render because of how redux form handles inputs through props.
  getInputProps = () => {
    const {
      handleBlur,
      props,
    } = this

    const {
      autoFocus,
      disabled,
      id,
      input,
      label,
      placeholder,
    } = props

    return {
      ...input,
      autoFocus,
      disabled,
      id,
      onBlur: handleBlur,
      placeholder: placeholder || label || '',
    }
  }

  render () {
    const {
      getInputProps,
      getSuggestionValue,
      makeFilteredSuggestions,
      onSuggestionsFetchRequested,
      props,
      renderSuggestion,
      state: { fetching },
      onSuggestionSelected,
    } = this

    const {
      alwaysRenderSuggestions,
      id,
      label,
      meta,
      renderSuggestionsContainer,
    } = props

    const {
      asyncValidating,
      error,
    } = meta

    // Autosuggest requires onSuggestionsClearRequested however
    // it is not necessary with this implementation.
    return (
      <div className={'auto-suggest labeled-input flex-wrap-wrap'}>
        { label && <label id={`${id}-label`}>{label}</label> }
        <div className={asyncValidating || fetching ? 'async-validating' : ''}>
          <ReactAutoSuggest
            alwaysRenderSuggestions={alwaysRenderSuggestions}
            id={id} // id is given to the auto-suggest container.
            inputProps={getInputProps()}
            onSuggestionSelected={onSuggestionSelected}
            onSuggestionsClearRequested={e => e}
            getSuggestionValue={getSuggestionValue}
            onSuggestionsFetchRequested={onSuggestionsFetchRequested}
            renderSuggestion={renderSuggestion}
            renderSuggestionsContainer={renderSuggestionsContainer}
            shouldRenderSuggestions={() => true} // because of how the parent wrapper handles, this is always true
            suggestions={makeFilteredSuggestions()} />
          <i className={'fa fa-spinner fa-pulse fa-fw'} />
          <ErrorSimple
            error={error}
            idRoot={id} />
        </div>
      </div>
    )
  }
}

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    error,
  }, dispatch)
)

export default connect(
  () => ({}),
  mapDispatchToProps
)(AutoSuggest)
